/* eslint-disable @typescript-eslint/no-explicit-any */
import { Card, CardHeader, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import {
  useDunningRulesQuery,
  useCreateDunningRuleMutation,
  useUpdateDunningRuleMutation,
  useDeleteDunningRuleMutation,
  useRunRuleNowMutation,
  usePreviewRuleQuery,
} from '@/hooks/useDunning'
import { Label } from '@/components/ui/label'
import AppDialog, { ModalActions } from '@/components/CustomModal'
import {
  useOwnerSettingsQuery,
  useUpdateOwnerSettingsMutation,
} from '@/hooks/useOwnerSettings'
import { useNotificationsQuery } from '@/hooks/useNotifications'
import { formatDateFR } from '@/lib/utils'
import { useMemo, useState } from 'react'
import { toast } from 'sonner'

function statusLabel(s: string) {
  switch (s) {
    case 'QUEUED':
      return 'En file'
    case 'SENT':
      return 'Envoyé'
    case 'DELIVERED':
      return 'Livré'
    case 'READ':
      return 'Lu'
    case 'FAILED':
      return 'Échec'
    default:
      return s
  }
}

function statusTone(s: string) {
  switch (s) {
    case 'FAILED':
      return 'border-red-300 bg-red-50 text-red-900 dark:border-red-900/40 dark:bg-red-950/30 dark:text-red-300'
    case 'READ':
    case 'DELIVERED':
      return 'border-emerald-300 bg-emerald-50 text-emerald-900 dark:border-emerald-900/40 dark:bg-emerald-950/30 dark:text-emerald-300'
    case 'SENT':
      return 'border-blue-300 bg-blue-50 text-blue-900 dark:border-blue-900/40 dark:bg-blue-950/30 dark:text-blue-300'
    default:
      return 'border-muted bg-muted/20 text-foreground/80'
  }
}

const DEFAULT_FORM = {
  name: '',
  channel: 'WHATSAPP' as 'EMAIL' | 'WHATSAPP',
  template: 'DEFAULT' as 'DEFAULT' | 'SOFT' | 'HARD',
  frequencyDays: 7,
  runHour: '09:00',
}

export default function DunningRulesCard() {
  const { data: rules, status } = useDunningRulesQuery(true)
  const [previewId, setPreviewId] = useState<string | null>(null)
  const { data: preview } = usePreviewRuleQuery(previewId ?? undefined)
  const [createOpen, setCreateOpen] = useState(false)
  const [form, setForm] = useState(DEFAULT_FORM)
  const createMut = useCreateDunningRuleMutation()

  // ✅ Toggle global
  const settingsQ = useOwnerSettingsQuery(true)
  const updateSettings = useUpdateOwnerSettingsMutation()
  const dunningEnabled = settingsQ.data?.dunningEnabled ?? true

  // ✅ History
  const notifQ = useNotificationsQuery(
    { type: 'CUSTOMER_DUNNING', take: 50 },
    status !== 'pending'
  )

  const autoBadge = useMemo(() => {
    return dunningEnabled ? (
      <span className='text-xs rounded-full border px-2 py-0.5'>
        Auto activé
      </span>
    ) : (
      <span className='text-xs rounded-full border px-2 py-0.5 border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-300'>
        Auto désactivé
      </span>
    )
  }, [dunningEnabled])

  const handleCreate = async () => {
    if (!form.name.trim()) {
      toast.error('Nom requis')
      return
    }
    try {
      await createMut.mutateAsync(form)
      toast.success('Règle créée')
      setCreateOpen(false)
      setForm(DEFAULT_FORM)
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? 'Erreur')
    }
  }

  if (status === 'pending') return null

  return (
    <Card>
      <CardHeader>
        <div className='flex items-start justify-between gap-3'>
          <div>
            <h3 className='text-lg font-semibold'>Règles de relance</h3>
            <p className='text-xs text-muted-foreground'>
              Relances automatiques (Email/WhatsApp)
            </p>
          </div>
          <div className='flex items-center gap-2'>
            {autoBadge}
            <Button size='sm' onClick={() => setCreateOpen(true)}>
              + Nouvelle règle
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className='space-y-4'>
        {/* ✅ Toggle global */}
        <div className='rounded-md border p-3 flex items-center justify-between'>
          <div>
            <div className='text-sm font-medium'>Relances automatiques</div>
            <div className='text-xs text-muted-foreground'>
              Désactiver coupe seulement l'automatique. L'envoi manuel reste
              disponible.
            </div>
          </div>

          <Switch
            checked={dunningEnabled}
            disabled={settingsQ.isFetching || updateSettings.isPending}
            onCheckedChange={async (v) => {
              try {
                await updateSettings.mutateAsync({ dunningEnabled: v })
                toast.success(
                  v
                    ? 'Relances automatiques activées'
                    : 'Relances automatiques désactivées'
                )
              } catch (e: any) {
                toast.error('Impossible de modifier', {
                  description: e?.response?.data?.message ?? 'Erreur inconnue',
                })
              }
            }}
          />
        </div>

        {/* Info when auto off */}
        {!dunningEnabled ? (
          <div className='rounded-md border p-3 text-sm'>
            <div className='font-medium'>Automatique désactivé</div>
            <div className='text-xs text-muted-foreground mt-1'>
              Les règles ne s'exécuteront pas automatiquement tant que ce
              paramètre est OFF. Vous pouvez toujours utiliser <b>Run now</b>{' '}
              pour déclencher manuellement.
            </div>
          </div>
        ) : null}

        {/* Rules */}
        {!rules?.length ? (
          <div className='text-sm text-muted-foreground'>
            Aucune règle. Créez-en une avec "+ Nouvelle règle".
          </div>
        ) : (
          rules.map((r) => (
            <RuleRow
              key={r.id}
              ruleId={r.id}
              name={r.name}
              enabled={r.enabled}
              channel={r.channel}
              frequencyDays={r.frequencyDays ?? 7}
              setPreviewId={setPreviewId}
            />
          ))
        )}

        {/* Dialog création */}
        <AppDialog
          open={createOpen}
          onOpenChange={setCreateOpen}
          title='Nouvelle règle de relance'
          description='Configurez le canal, le ton et la fréquence.'
          footer={
            <ModalActions
              confirmLabel='Créer'
              confirmLoading={createMut.isPending}
              onConfirm={handleCreate}
            />
          }
        >
          <div className='px-4 py-2 space-y-4'>
            <div className='space-y-1'>
              <Label>Nom</Label>
              <input
                className='w-full rounded-md border px-3 py-2 text-sm'
                placeholder='Ex: Relance WhatsApp hebdo'
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              />
            </div>

            <div className='space-y-1'>
              <Label>Canal</Label>
              <div className='flex gap-2'>
                {(['WHATSAPP', 'EMAIL'] as const).map((c) => (
                  <Button
                    key={c}
                    type='button'
                    size='sm'
                    variant={form.channel === c ? 'default' : 'outline'}
                    onClick={() => setForm((f) => ({ ...f, channel: c }))}
                  >
                    {c === 'WHATSAPP' ? 'WhatsApp' : 'Email'}
                  </Button>
                ))}
              </div>
            </div>

            <div className='space-y-1'>
              <Label>Ton du message</Label>
              <div className='flex gap-2'>
                {([['DEFAULT', 'Standard'], ['SOFT', 'Souple'], ['HARD', 'Ferme']] as const).map(([val, label]) => (
                  <Button
                    key={val}
                    type='button'
                    size='sm'
                    variant={form.template === val ? 'default' : 'outline'}
                    onClick={() => setForm((f) => ({ ...f, template: val }))}
                  >
                    {label}
                  </Button>
                ))}
              </div>
            </div>

            <div className='space-y-1'>
              <Label>Fréquence</Label>
              <div className='flex gap-2 flex-wrap'>
                {FREQUENCY_OPTIONS.map((opt) => (
                  <Button
                    key={opt.value}
                    type='button'
                    size='sm'
                    variant={form.frequencyDays === opt.value ? 'default' : 'outline'}
                    onClick={() => setForm((f) => ({ ...f, frequencyDays: opt.value }))}
                  >
                    {opt.label}
                  </Button>
                ))}
              </div>
            </div>

            <div className='space-y-1'>
              <Label>Heure d'envoi</Label>
              <input
                type='time'
                className='rounded-md border px-3 py-2 text-sm'
                value={form.runHour}
                onChange={(e) => setForm((f) => ({ ...f, runHour: e.target.value }))}
              />
            </div>
          </div>
        </AppDialog>

        {/* Preview targets */}
        {previewId && (
          <div className='rounded-md border p-3'>
            <div className='flex items-center justify-between mb-2'>
              <div className='text-sm font-medium'>Aperçu cibles</div>
              <Button
                variant='ghost'
                size='sm'
                onClick={() => setPreviewId(null)}
              >
                Fermer
              </Button>
            </div>

            <div className='max-h-56 overflow-auto text-sm'>
              {preview?.length ? (
                preview.map((c) => (
                  <div
                    key={c.id}
                    className='flex items-center justify-between py-1 border-b last:border-b-0'
                  >
                    <div>
                      <div className='font-medium'>{c.name}</div>
                      <div className='text-xs text-muted-foreground'>
                        {(c as any).phone ?? (c as any).email ?? '—'}
                      </div>
                    </div>
                    <div className='text-xs'>
                      Dette: {c.debt.toLocaleString('fr-FR')} CFA
                    </div>
                  </div>
                ))
              ) : (
                <div className='text-muted-foreground'>Aucune cible.</div>
              )}
            </div>
          </div>
        )}

        {/* ✅ History */}
        <div className='rounded-md border p-3'>
          <div className='flex items-center justify-between mb-2'>
            <div className='text-sm font-medium'>Dernières relances</div>
            <Button
              variant='ghost'
              size='sm'
              onClick={() => notifQ.refetch()}
              disabled={notifQ.isFetching}
            >
              Actualiser
            </Button>
          </div>

          <div className='max-h-72 overflow-auto text-sm'>
            {notifQ.status === 'pending' ? (
              <div className='text-muted-foreground'>Chargement…</div>
            ) : !notifQ.data?.length ? (
              <div className='text-muted-foreground'>Aucune relance.</div>
            ) : (
              notifQ.data.map((n) => {
                const to = n.meta?.to ?? '—'
                const label = n.meta?.customerName ?? to
                const debt = n.meta?.debt

                return (
                  <div key={n.id} className='py-2 border-b last:border-b-0'>
                    <div className='flex items-center justify-between gap-2'>
                      <div className='min-w-0'>
                        <div className='font-medium truncate'>{label}</div>
                        <div className='text-xs text-muted-foreground'>
                          {n.channel} •{' '}
                          {formatDateFR(n.createdAt, { showTime: 'auto' })}
                        </div>
                      </div>

                      <span
                        className={
                          'shrink-0 text-xs rounded-full border px-2 py-0.5 ' +
                          statusTone(n.status)
                        }
                      >
                        {statusLabel(n.status)}
                      </span>
                    </div>

                    <div className='mt-1 flex items-center justify-between text-xs text-muted-foreground'>
                      <span>
                        {typeof debt === 'number'
                          ? `Dette: ${Math.round(debt).toLocaleString(
                              'fr-FR'
                            )} CFA`
                          : null}
                      </span>
                      {n.error ? (
                        <span className='text-red-600'>{n.error}</span>
                      ) : null}
                    </div>
                  </div>
                )
              })
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

const FREQUENCY_OPTIONS = [
  { label: '3 jours', value: 3 },
  { label: '1 semaine', value: 7 },
  { label: '2 semaines', value: 14 },
  { label: '1 mois', value: 30 },
]

function RuleRow({
  ruleId,
  name,
  enabled,
  channel,
  frequencyDays,
  setPreviewId,
}: {
  ruleId: string
  name: string
  enabled: boolean
  channel: 'EMAIL' | 'WHATSAPP'
  frequencyDays: number
  setPreviewId: (id: string | null) => void
}) {
  const updateMut = useUpdateDunningRuleMutation(ruleId)
  const deleteMut = useDeleteDunningRuleMutation()
  const runMut = useRunRuleNowMutation(ruleId)

  const handleDelete = async () => {
    if (!window.confirm('Supprimer cette règle ?')) return
    try {
      await deleteMut.mutateAsync(ruleId)
      toast.success('Règle supprimée')
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? 'Erreur')
    }
  }

  const toggle = async () => {
    try {
      await updateMut.mutateAsync({ enabled: !enabled })
    } catch (e: any) {
      toast.error(
        e?.response?.data?.message ?? 'Impossible de modifier la règle'
      )
    }
  }

  const runNow = async () => {
    const ok = window.confirm(
      'Exécuter la règle maintenant ? Cela enverra des relances aux clients ciblés.'
    )
    if (!ok) return

    try {
      await runMut.mutateAsync()
      toast.success('Relances déclenchées')
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? 'Échec du run')
    }
  }

  const setFrequency = async (days: number) => {
    try {
      await updateMut.mutateAsync({ frequencyDays: days } as any)
      toast.success('Fréquence mise à jour')
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? 'Impossible de modifier')
    }
  }

  return (
    <div className='rounded-md border p-3 space-y-3'>
      <div className='flex items-center justify-between'>
        <div>
          <div className='font-medium'>{name}</div>
          <div className='text-xs text-muted-foreground'>
            Canal: {channel} • ID: {ruleId.slice(0, 8)}…
          </div>
        </div>
        <div className='flex gap-2'>
          <Button
            variant='secondary'
            size='sm'
            onClick={() => setPreviewId(ruleId)}
          >
            Preview
          </Button>
          <Button
            variant='outline'
            size='sm'
            onClick={toggle}
            disabled={updateMut.isPending}
          >
            {enabled ? 'Désactiver' : 'Activer'}
          </Button>
          <Button size='sm' onClick={runNow} disabled={runMut.isPending}>
            Run now
          </Button>
          <Button
            variant='destructive'
            size='sm'
            onClick={handleDelete}
            disabled={deleteMut.isPending}
          >
            Supprimer
          </Button>
        </div>
      </div>

      <div className='space-y-1'>
        <div className='text-xs text-muted-foreground font-medium'>
          Fréquence de relance
        </div>
        <div className='flex gap-2 flex-wrap'>
          {FREQUENCY_OPTIONS.map((opt) => (
            <Button
              key={opt.value}
              type='button'
              size='sm'
              variant={frequencyDays === opt.value ? 'default' : 'outline'}
              disabled={updateMut.isPending}
              onClick={() => setFrequency(opt.value)}
            >
              {opt.label}
            </Button>
          ))}
        </div>
      </div>
    </div>
  )
}
